import React, { useEffect, useState } from "react";
import { Lock, Loader2, ShieldCheck, KeyRound, Sparkles, MessageCircle, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { SITE } from "../../i18n/translations";

const CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadCheckout() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = CHECKOUT_SRC;
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("Could not load the payment window. Please check your connection."));
    document.body.appendChild(s);
  });
}

const FEATURES = [
  "House-by-house reading of all 12 houses",
  "Career, love, marriage, finance, education, family, spiritual life",
  "Your running Mahadasha & Antardasha explained",
  "Calm, traditional dosha notes — never fear-based",
  "5-7 personalised traditional remedies",
];

export const UnlockPanel = ({ report, lang = "en", onUnlocked }) => {
  const [pricing, setPricing] = useState(null);
  const [stage, setStage] = useState("locked"); // locked | contact | paying | code
  const [contact, setContact] = useState({ name: report?.name || "", phone: "", email: "" });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recover, setRecover] = useState("");
  const [showRecover, setShowRecover] = useState(false);

  useEffect(() => {
    api.get("/astro/pricing").then((r) => setPricing(r.data)).catch(() => {});
    const pending = localStorage.getItem("tt_astro_pending_code");
    if (pending) {
      setCode(pending);
      setStage("code");
      localStorage.removeItem("tt_astro_pending_code");
    }
  }, []);

  const price = pricing?.amount_display || "₹199";

  const startPayment = async () => {
    if (contact.name.trim().length < 2) return toast.error("Please enter your name");
    if (contact.phone.replace(/\D/g, "").length < 10) return toast.error("Please enter a valid 10-digit WhatsApp number");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) return toast.error("Please enter a valid email address");

    setBusy(true);
    try {
      await loadCheckout();
      const { data: order } = await api.post("/astro/payment/order", {
        report_id: report.report_id, ...contact, lang,
      });
      if (order.already_paid) {
        toast.info(order.message);
        setStage("code");
        return;
      }
      setStage("paying");
      const rzp = new window.Razorpay({
        key: order.key_id,
        order_id: order.gateway_order_id,
        amount: order.amount,
        currency: order.currency,
        name: SITE.name,
        description: order.label,
        prefill: order.prefill,
        notes: { report_ref: order.report_ref },
        theme: { color: "#0E5A5E" },
        handler: async (resp) => {
          try {
            const { data } = await api.post("/astro/payment/verify", {
              report_id: report.report_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            sessionStorage.setItem(`tt_pay_${order.gateway_order_id}`, JSON.stringify(data));
            window.location.assign(
              `/astrology/unlock-success?order=${encodeURIComponent(order.gateway_order_id)}&report=${encodeURIComponent(report.report_id)}`);
          } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
            setStage("code");
          }
        },
        modal: {
          ondismiss: () => {
            api.post("/astro/payment/cancel", { gateway_order_id: order.gateway_order_id }).catch(() => {});
            toast.info("Payment cancelled. Your report is still waiting for you.");
            setStage("contact");
          },
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "The payment did not go through. Nothing was charged.");
        setStage("contact");
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      setStage("contact");
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async () => {
    if (!code.trim()) return toast.error("Please enter your unlock code");
    setBusy(true);
    try {
      const { data } = await api.post("/astro/unlock", { report_id: report.report_id, unlock_code: code });
      toast.success("Your full report is unlocked.");
      onUnlocked(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitRecover = async () => {
    if (!recover.trim()) return toast.error("Enter the WhatsApp number or email you paid with");
    setBusy(true);
    try {
      const { data } = await api.post("/astro/recover-code", { report_id: report.report_id, contact: recover });
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const waLink = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(
    `Namaste, I would like to unlock my Full Astrology Report (${report?.report_ref || ""}). Please guide me with the payment.`)}`;

  return (
    <div className="glass-strong relative overflow-hidden rounded-[1.6rem] p-7 sm:p-10" data-testid="report-unlock">
      <span className="orb orb-gold h-56 w-56 -top-20 left-1/3" />
      <div className="relative mx-auto max-w-3xl">
        <div className="text-center">
          <span className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-tara-tealDeep to-tara-vermilion">
            <Lock className="h-6 w-6 text-white" />
          </span>
          <h3 className="font-serif text-2xl sm:text-3xl font-bold text-tara-ink mb-3">
            Unlock your complete reading
          </h3>
          <p className="text-sm text-tara-body leading-relaxed mb-2">
            {pricing?.description || "The detailed Full Astrology Report is prepared for your chart alone."}
          </p>
          {report?.report_ref && (
            <p className="text-[11px] font-mono text-tara-muted" data-testid="unlock-report-ref">
              Report reference · {report.report_ref}
            </p>
          )}
        </div>

        <ul className="mx-auto mt-7 grid max-w-xl gap-2.5 sm:grid-cols-2">
          {FEATURES.map((f) => (
            <li key={f} className="flex gap-2 text-sm text-tara-body">
              <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-tara-teal" />{f}
            </li>
          ))}
        </ul>

        {/* --- Stage: locked --- */}
        {stage === "locked" && (
          <div className="mt-8 flex flex-col items-center gap-3">
            {pricing && !pricing.payments_enabled ? (
              <div className="w-full rounded-2xl border border-amber-300/60 bg-amber-50/70 p-5 text-center" data-testid="payments-disabled-note">
                <p className="text-sm font-semibold text-amber-900 mb-1">Online payment is being activated</p>
                <p className="text-xs text-amber-900/80 leading-relaxed mb-4">
                  Message us on WhatsApp to complete your {price} payment and we will send your unique
                  unlock code straight away.
                </p>
                <a href={waLink} target="_blank" rel="noreferrer"
                   className="btn-sacred inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-serif font-semibold"
                   data-testid="unlock-whatsapp-btn">
                  <MessageCircle className="h-4 w-4" /> Pay {price} on WhatsApp
                </a>
              </div>
            ) : (
              <button onClick={() => setStage("contact")}
                      className="btn-sacred flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-serif font-semibold"
                      data-testid="unlock-pay-btn">
                <Lock className="h-4 w-4" /> Unlock Full Astrology Report — {price}
              </button>
            )}
            <button onClick={() => setStage("code")}
                    className="btn-ghost-gold flex items-center gap-2 rounded-full px-6 py-3 text-sm font-serif font-semibold"
                    data-testid="have-code-btn">
              <KeyRound className="h-4 w-4" /> I already have an unlock code
            </button>
            <p className="mt-2 flex items-center gap-1.5 text-[11px] text-tara-muted">
              <ShieldCheck className="h-3.5 w-3.5 text-tara-teal" /> One-time payment · secure gateway · code linked only to this report
            </p>
          </div>
        )}

        {/* --- Stage: contact details --- */}
        {stage === "contact" && (
          <div className="mx-auto mt-8 max-w-md" data-testid="unlock-contact-form">
            <p className="mb-4 text-center text-xs text-tara-muted leading-relaxed">
              Your unlock code will be sent to this WhatsApp number and email, and shown on the
              confirmation page.
            </p>
            <div className="space-y-3">
              <input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })}
                     placeholder="Your name"
                     className="w-full rounded-2xl border border-tara-teal/25 bg-white/85 px-5 py-3.5 text-sm text-tara-ink placeholder:text-tara-muted/70 outline-none focus:border-tara-teal/60"
                     data-testid="pay-name-input" />
              <input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                     placeholder="WhatsApp number (10 digits)" inputMode="tel"
                     className="w-full rounded-2xl border border-tara-teal/25 bg-white/85 px-5 py-3.5 text-sm text-tara-ink placeholder:text-tara-muted/70 outline-none focus:border-tara-teal/60"
                     data-testid="pay-phone-input" />
              <input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })}
                     placeholder="Email address" inputMode="email"
                     className="w-full rounded-2xl border border-tara-teal/25 bg-white/85 px-5 py-3.5 text-sm text-tara-ink placeholder:text-tara-muted/70 outline-none focus:border-tara-teal/60"
                     data-testid="pay-email-input" />
            </div>
            <button onClick={startPayment} disabled={busy}
                    className="btn-sacred mt-4 flex w-full items-center justify-center gap-2 rounded-full px-7 py-4 text-sm font-serif font-semibold"
                    data-testid="pay-now-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Pay {price} securely
            </button>
            <div className="mt-3 flex justify-center gap-4 text-[11px]">
              <button onClick={() => setStage("locked")} className="text-tara-muted hover:text-tara-ink" data-testid="pay-back-btn">Back</button>
              <button onClick={() => setStage("code")} className="text-tara-tealDeep hover:underline" data-testid="pay-have-code-btn">I already have a code</button>
            </div>
            <p className="mt-4 text-[11px] text-tara-muted leading-relaxed text-center">
              We never store your card details. Your number and email are used only for your report and
              its delivery.
            </p>
          </div>
        )}

        {/* --- Stage: paying --- */}
        {stage === "paying" && (
          <div className="mt-8 text-center" data-testid="pay-pending">
            <Loader2 className="mx-auto h-7 w-7 animate-spin text-tara-tealDeep" />
            <p className="mt-3 text-sm text-tara-body">Waiting for the payment window…</p>
          </div>
        )}

        {/* --- Stage: enter code --- */}
        {stage === "code" && (
          <div className="mx-auto mt-8 max-w-md" data-testid="unlock-code-form">
            <label className="mb-2 block text-center text-[11px] uppercase tracking-[0.18em] text-tara-muted">
              Enter your unlock code
            </label>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                     placeholder="AST-XXXX-XXXX" autoComplete="one-time-code"
                     className="flex-1 rounded-full border border-tara-teal/25 bg-white/85 px-5 py-3.5 text-center font-mono text-sm tracking-[0.2em] text-tara-ink placeholder:tracking-normal placeholder:text-tara-muted/70 outline-none focus:border-tara-teal/60"
                     data-testid="unlock-code-input" />
              <button onClick={submitCode} disabled={busy}
                      className="btn-sacred flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-serif font-semibold"
                      data-testid="unlock-code-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Unlock my report
              </button>
            </div>
            <div className="mt-3 flex justify-center gap-4 text-[11px]">
              <button onClick={() => setStage("locked")} className="text-tara-muted hover:text-tara-ink" data-testid="code-back-btn">Back</button>
              <button onClick={() => setShowRecover((s) => !s)} className="flex items-center gap-1 text-tara-tealDeep hover:underline" data-testid="recover-toggle-btn">
                <HelpCircle className="h-3 w-3" /> Lost your code?
              </button>
            </div>
            {showRecover && (
              <div className="mt-4 rounded-2xl border border-tara-teal/20 bg-white/70 p-4" data-testid="recover-form">
                <p className="mb-3 text-[11px] text-tara-muted leading-relaxed">
                  Enter the WhatsApp number or email you paid with and we will send the same code again.
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input value={recover} onChange={(e) => setRecover(e.target.value)}
                         placeholder="WhatsApp number or email"
                         className="flex-1 rounded-full border border-tara-teal/25 bg-white/85 px-4 py-2.5 text-sm outline-none focus:border-tara-teal/60"
                         data-testid="recover-input" />
                  <button onClick={submitRecover} disabled={busy}
                          className="btn-ghost-gold rounded-full px-5 py-2.5 text-sm font-serif font-semibold"
                          data-testid="recover-btn">
                    Resend my code
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UnlockPanel;
